import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatImageUrl } from '../utils/helpers';
import { Camera, Sparkles } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/gallery`);
      setGalleryItems(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery items:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (item) => {
    setSelectedImage(item);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="section-title text-4xl md:text-5xl mb-4">
            <span>Galéria</span>
          </h1>
          <p className="text-gray-300 text-lg">Fedezd fel a képeinket és történeteinket</p>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-200"></div>
            <p className="mt-4 text-gray-400">Betöltés...</p>
          </div>
        ) : galleryItems.length === 0 ? (
          <div className="text-center py-20 glass rounded-2xl">
            <Camera className="w-16 h-16 text-yellow-200/50 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Hamarosan érkeznek képek...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryItems.map((item) => (
              <div
                key={item.id}
                onClick={() => openLightbox(item)}
                className="group glass rounded-2xl overflow-hidden cursor-pointer hover:border-yellow-200/50 transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-purple-900/20 to-black">
                  <img
                    src={formatImageUrl(item.image_url)}
                    alt={item.caption}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Hover overlay with sparkle icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Sparkles className="w-12 h-12 text-yellow-200 animate-pulse" />
                  </div>
                </div>

                {/* Caption */}
                {item.caption && (
                  <div className="p-4">
                    <p className="text-gray-300 text-sm line-clamp-2">{item.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white text-4xl hover:text-yellow-200 transition"
            >
              ✕
            </button>

            {/* Image */}
            <img
              src={formatImageUrl(selectedImage.image_url)}
              alt={selectedImage.caption}
              className="w-full h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Caption */}
            {selectedImage.caption && (
              <div className="glass rounded-lg p-4 mt-4" onClick={(e) => e.stopPropagation()}>
                <p className="text-white text-center">{selectedImage.caption}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
};

export default Gallery;
